import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { adminApi } from "@/lib/api";
import { eur } from "@/lib/format";

const STATUS_COLORS = {
  pending: "text-amber-400", paid: "text-emerald-400",
  awaiting_measurements: "text-orange-300", in_design: "text-purple-300", crafting: "text-pink-300",
  processing: "text-sky-400",
  shipped: "text-[#D4AF37]", delivered: "text-emerald-300", cancelled: "text-red-400", refunded: "text-red-300",
};

export default function Orders() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [form, setForm] = useState({ status: "", carrier: "", tracking_number: "", tracking_url: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi.get("/admin/orders", { params: { page, limit: 15, status: statusFilter || undefined } })
      .then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(load, [page, statusFilter]);
  useEffect(() => {
    adminApi.get("/admin/orders/meta/statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
  }, []);

  const openOrder = (o) => {
    setSelected(o);
    setForm({
      status: o.status,
      carrier: o.fulfillment?.carrier || "",
      tracking_number: o.fulfillment?.tracking_number || "",
      tracking_url: o.fulfillment?.tracking_url || "",
      notes: o.fulfillment?.notes || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await adminApi.patch(`/admin/orders/${selected.id}`, {
        status: form.status,
        fulfillment: {
          carrier: form.carrier, tracking_number: form.tracking_number,
          tracking_url: form.tracking_url, notes: form.notes,
        },
      });
      setSelected(updated);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="ee-overline animate-pulse">Loading orders…</p>;

  const inputCls = "w-full bg-transparent border-b ee-hairline focus:border-[#D4AF37] outline-none py-2 text-[#FAFAF9] transition-colors placeholder:text-[#57534E] text-sm";

  return (
    <div data-testid="admin-orders">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="ee-overline mb-2">Fulfillment</p>
          <h1 className="font-display text-4xl tracking-tight">Orders</h1>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          data-testid="orders-status-filter"
          className="bg-[#1C1917] border ee-hairline px-4 py-2 text-sm text-[#A8A29E] outline-none"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="border ee-hairline bg-[#1C1917] overflow-hidden">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="text-left text-[#57534E] uppercase tracking-[0.12em] text-xs border-b ee-hairline">
              <th className="px-5 py-4">Order</th>
              <th className="px-5 py-4">Customer</th>
              <th className="px-5 py-4">Total</th>
              <th className="px-5 py-4">Payment</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Date</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} onClick={() => openOrder(o)} data-testid={`order-row-${o.order_number}`}
                  className="border-b ee-hairline last:border-0 hover:bg-[#0C0A09] cursor-pointer transition-colors">
                <td className="px-5 py-4 ee-gold-text">{o.order_number}</td>
                <td className="px-5 py-4 text-[#A8A29E]">{o.customer?.name}<br /><span className="text-xs text-[#57534E]">{o.customer?.email}</span></td>
                <td className="px-5 py-4">{eur(o.total)}</td>
                <td className="px-5 py-4 text-[#A8A29E]">{o.payment_status}</td>
                <td className={`px-5 py-4 capitalize ${STATUS_COLORS[o.status] || "text-[#A8A29E]"}`}>{o.status}</td>
                <td className="px-5 py-4 text-[#57534E]">{o.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {data.orders.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-[#57534E]">No orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-6 text-sm text-[#A8A29E]">
        <span>{data.total} orders · page {data.page} of {data.pages || 1}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border ee-hairline p-2 disabled:opacity-40 hover:border-[#D4AF37]"><ChevronLeft size={16} /></button>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="border ee-hairline p-2 disabled:opacity-40 hover:border-[#D4AF37]"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-detail">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg bg-[#1C1917] border-l ee-hairline h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="ee-overline">Order</p>
                <h2 className="font-display text-3xl ee-gold-text">{selected.order_number}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A8A29E] hover:text-[#D4AF37]"><X /></button>
            </div>

            <div className="space-y-6 text-sm">
              <div>
                <p className="ee-overline mb-3">Items</p>
                {selected.items?.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 border-b ee-hairline">
                    <span className="text-[#A8A29E]">{it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span>
                    <span>{eur(it.unit_price * it.quantity)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 font-display text-xl">
                  <span>Total</span><span className="ee-gold-text">{eur(selected.total)}</span>
                </div>
              </div>

              <div>
                <p className="ee-overline mb-3">Customer</p>
                <p className="text-[#A8A29E]">{selected.customer?.name} · {selected.customer?.email} · {selected.customer?.phone}</p>
                <p className="text-[#A8A29E] mt-2">
                  {[selected.customer?.address?.line1, selected.customer?.address?.line2, selected.customer?.address?.city,
                    selected.customer?.address?.region, selected.customer?.address?.postal_code, selected.customer?.address?.country]
                    .filter(Boolean).join(", ")}
                </p>
              </div>

              {selected.is_bespoke && (
                <div className="border ee-hairline bg-[#0C0A09] p-4" data-testid="order-bespoke">
                  <p className="ee-overline mb-2 ee-gold-text">Bespoke order · contact for measurements</p>
                  <p className="text-[#A8A29E] text-xs">
                    {selected.personalisation
                      ? <><span className="text-[#57534E]">Customer note:</span> "{selected.personalisation}"</>
                      : "No personalisation notes provided — reach out to capture measurements & custom details."}
                  </p>
                </div>
              )}

              <div className="border-t ee-hairline pt-6">
                <p className="ee-overline mb-4">Update fulfillment</p>
                <label className="text-xs text-[#57534E]">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="order-status-select" className="w-full bg-[#0C0A09] border ee-hairline px-3 py-2 text-sm mt-1 mb-4 outline-none">
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <input placeholder="Carrier" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className={inputCls} data-testid="order-carrier" />
                  <input placeholder="Tracking #" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className={inputCls} data-testid="order-tracking" />
                </div>
                <input placeholder="Tracking URL" value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} className={`${inputCls} mb-3`} />
                <textarea placeholder="Internal notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} />
                <button onClick={save} disabled={saving} data-testid="order-save-btn" className="w-full mt-5 bg-[#D4AF37] text-[#0C0A09] py-3 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors disabled:opacity-60">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
